import { useEffect, useRef, useState } from 'react';

/**
 * Custom hook for dynamically loading the Add to Calendar Button CDN script
 * Uses singleton pattern to ensure script is only loaded once across all components
 *
 * @param {boolean} enabled - Whether the add-to-calendar functionality should be enabled
 * @returns {Object} Hook state object
 * @returns {boolean} isLoaded - Whether the script has been successfully loaded
 * @returns {boolean} isLoading - Whether the script is currently being loaded
 * @returns {boolean} hasError - Whether there was an error loading the script
 */
export const useAddToCalendarScript = (enabled = true) => {
  const loadAttempted = useRef(false);
  const scriptElement = useRef(null);
  const [scriptState, setScriptState] = useState({
    isLoaded: false,
    isLoading: false,
    hasError: false,
  });

  const loadScript = () => {
    if (scriptState.isLoaded || scriptState.isLoading || !enabled) return;

    setScriptState(prev => ({ ...prev, isLoading: true }));

    try {
      scriptElement.current = document.createElement('script');
      scriptElement.current.src = 'https://cdn.jsdelivr.net/npm/add-to-calendar-button@2';
      scriptElement.current.async = true;
      scriptElement.current.defer = true;

      scriptElement.current.onload = () => {
        setScriptState({ isLoaded: true, isLoading: false, hasError: false });
      };

      scriptElement.current.onerror = () => {
        setScriptState(prev => ({ ...prev, isLoading: false, hasError: true }));
      };

      // Find existing script tag or add to head
      const existingScript = document.querySelector(
        'script[src="https://cdn.jsdelivr.net/npm/add-to-calendar-button@2"]'
      );
      if (!existingScript) {
        document.head.appendChild(scriptElement.current);
      } else {
        // Script already exists, update our tracking
        scriptElement.current = existingScript;
        if (existingScript.readyState === 'complete' || existingScript.readyState === 'loaded') {
          setScriptState({ isLoaded: true, isLoading: false, hasError: false });
        } else {
          // Script exists (maybe loaded by other component) -> we need to know when it loads to update our state
          scriptElement.current.addEventListener('load', () => {
            setScriptState(prev => ({ ...prev, isLoading: false, isLoaded: true }));
          });

          scriptElement.current.addEventListener('error', () => {
            setScriptState(prev => ({ ...prev, isLoading: false, hasError: true }));
          });
        }
      }
    } catch (error) {
      setScriptState(prev => ({ ...prev, isLoading: false, hasError: true }));
      console.error('Error creating Add to Calendar Button script:', error);
    }
  };

  useEffect(() => {
    if (!enabled) return;

    // If script is already loaded, no need to do anything
    if (scriptState.isLoaded) {
      setScriptState({ isLoaded: true, isLoading: false, hasError: false });
      return;
    }
    // If script is not loading and we haven't attempted to load it, start loading
    if (!scriptState.isLoading && !loadAttempted.current) {
      loadAttempted.current = true;
      loadScript();
    }
  }, [enabled]);

  if (!enabled) {
    return { isLoaded: false, isLoading: false, hasError: false };
  }
  return scriptState;
};
